/**
 * Funnel route guard. Children render unconditionally; the session mint
 * runs in the background and surfaces failures as a non-blocking retry
 * banner. See `useFunnelLead.tsx` for the resilience contract.
 *
 * Variant is persisted to sessionStorage so it survives the Stripe 3DS
 * redirect on the payment page.
 */

import {
  POSITIONING_VARIANTS,
  POSITIONING_VARIANT_STORAGE_KEY,
  type PositioningVariant,
} from '@/constants/funnel';
import { useEnsureFunnelSession } from '@/hooks/useFunnelLead';
import useAnalyticsCalls from '@/hooks/use-analytics';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

function resolveVariantFromPath(pathname: string): PositioningVariant {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  if (segments[0] !== 'join') return 'default';
  const candidate = segments[1];
  if (candidate && (POSITIONING_VARIANTS as readonly string[]).includes(candidate)) {
    return candidate as PositioningVariant;
  }
  return 'default';
}

interface FunnelGuardProps {
  children?: ReactNode;
}

export function FunnelGuard({ children }: FunnelGuardProps) {
  const location = useLocation();
  const variant = useMemo(() => resolveVariantFromPath(location.pathname), [location.pathname]);

  useEffect(() => {
    sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, variant);
  }, [variant]);

  const { error, retry, isInitializing } = useEnsureFunnelSession(variant);

  const { setPositioningVariantProperty } = useAnalyticsCalls();
  const hasSetPropRef = useRef(false);
  useEffect(() => {
    if (!hasSetPropRef.current) {
      hasSetPropRef.current = true;
      setPositioningVariantProperty(variant);
    }
  }, [variant, setPositioningVariantProperty]);

  return (
    <>
      {error && !isInitializing && <FunnelSessionErrorBanner onRetry={retry} />}
      {children ?? <Outlet />}
    </>
  );
}

interface FunnelSessionErrorBannerProps {
  onRetry: () => void;
}

function FunnelSessionErrorBanner({ onRetry }: FunnelSessionErrorBannerProps) {
  return (
    <div
      role="status"
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
        <span>Having trouble connecting. Your info is safe — keep going and we’ll retry.</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-amber-300 bg-white px-3 py-1 font-medium text-amber-900 hover:bg-amber-100"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
